<template>
  <div :class="classResolver(classes)">
    <div
      v-if="hasGeneralSearch"
      id="general-search-container"
      class="generalSearch"
    >
      <label class="generalSearch__label" for="general-search">
        General Search
      </label>
      <input
        id="general-search"
        autofocus="autofocus"
        class="generalSearch__input"
        placeholder="General search..."
        type="search"
        @input="handleGeneralSearch"
      />
    </div>
    <div :id="componentId" ref="tabulatorElement" class="table-sm thead-dark" />
    <ItemSelector
      v-if="canSelectColumns"
      v-model:active="activeColumns"
      :available="availableColumns"
      title="Columns"
    />
  </div>
</template>
<script>
import { computed, nextTick, onBeforeUnmount, onMounted, onUpdated, ref, watch } from "vue";
import { DateTime } from "luxon";
import TabulatorColumnMapper from "@root/tabulator/Services/TabulatorColumnMapper";
// noinspection ES6CheckImport
import {
  AjaxModule,
  DataTreeModule,
  EditModule,
  FilterModule,
  FormatModule,
  InteractionModule,
  MutatorModule,
  PageModule,
  PersistenceModule,
  PopupModule,
  ResizeColumnsModule,
  SelectRowModule,
  SortModule,
  Tabulator,
  TooltipModule,
} from "tabulator-tables";
import NewtonTrailersModule from "@root/tabulator/Module/NewtonTrailersModule";
import tabulatorAjaxRequestViaApi, { clearTabulatorCache, } from "@root/tabulator/Services/tabulatorAjaxRequestViaApi";
import ItemSelector from "@root/components/ItemSelector.vue";
import useApi from "@apexl/icarus-core/src/services/api";
import classResolver from "@apexl/icarus-core/src/services/classResolver";
import classes from "@apexl/icarus-core/src/components/_properties/classes";
import resolveOnRowClickMethod from "@root/services/resolveOnRowClickMethod";
import resolveOnCellClickMethod from "@root/services/resolveOnCellClickMethod";
import clearSearchElementPolyfill from "@root/services/clearSearchElementPolyfill";
import { queryListener, read, readUrlQueryParams, write, } from "@root/tabulator/persistTabulatorInQuery";
import ajaxColumnLoader from "@root/services/ajaxColumnLoader";
import useLocalStorage from "@apexl/icarus-core/src/services/useLocalStorage";
import eventBus from "@apexl/icarus-core/src/services/eventBus";
import TabulatorRowFormatter from "@root/enums/TabulatorRowFormatter";
import { cygnusIconHtml } from "@root/services/cygnusIconMarkup";
import useTabulatorPageFilterStore from "@root/store/useTabulatorPageFilterStore";
import actionMapper from "@root/tabulator/Services/tabulatorActionMapper";
import { isPropagationStopped } from "@root/tabulator/Services/stopPropagation";
import { useRouter } from 'vue-router';


Tabulator.registerModule([
  AjaxModule,
  EditModule,
  FilterModule,
  FormatModule,
  InteractionModule,
  MutatorModule,
  PageModule,
  ResizeColumnsModule,
  SortModule,
  TooltipModule,
  PopupModule,
  SelectRowModule,
  PersistenceModule,
  NewtonTrailersModule,
  DataTreeModule,
]);

// noinspection JSUnusedGlobalSymbols
export default {
  methods: { classResolver },
  components: { ItemSelector },
  props: {
    ...classes,
    columns: {
      type: Array,
      default: () => [],
    },
    hasGeneralSearch: {
      type: Boolean,
      default: false,
    },
    layout: {
      type: String,
      default: "fitColumns",
    },
    pagination: {
      type: Boolean,
      default: true,
    },
    persistence: {
      type: Boolean,
      default: true,
    },
    ajaxUrl: {
      type: String,
      required: false,
      default: "",
    },
    url: {
      type: String,
      required: true,
    },
    paginationSizes: {
      type: Array,
      default: () => [20, 50, 100],
    },
    initialSort: {
      type: Array,
      default: () => [],
    },
    initialHeaderFilter: {
      type: Array,
      default: () => [],
    },
    canSelectColumns: {
      type: Boolean,
      default: true,
    },
    activeColumns: {
      type: Array,
      default: () => [],
    },
    hiddenColumns: {
      type: Array,
      default: () => [],
    },
    saveLayoutRoute: {
      type: String,
      required: false,
    },
    viewId: {
      type: String,
      required: true,
    },
    immutableFilters: {
      type: Array,
      required: false,
      default: () => [],
    },
    onRowClickActions: {
      type: Array,
      required: false,
      default: () => [],
    },
    onCellClickActions: {
      type: Array,
      required: false,
      default: () => [],
    },
    meta: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    componentId: {
      type: String,
      required: true,
    },
    subEntity: {
      type: String,
      default: "",
    },
    footerRowCount: {
      type: Number,
      default: 0,
    },
    hasTreeLayout: {
      type: Boolean,
      default: false,
    },
    shouldPollData: {
      type: Boolean,
      default: false,
    },
    actions: {
      type: Array,
      default: () => [],
    }
  },

  emits: ["tabulator:created", "tabulator:filtered"],

  setup(props, { emit }) {
    const router = useRouter();
    const tabulatorElement = ref(null);
    const tabulator = ref(null);
    const scrollX = useLocalStorage("tabulator-scroll-x", 0, true);
    const api = useApi();
    const availableColumns = computed(() =>
      props.columns
        .filter(({ visible = true }) => visible)
        .map(({ field, title }) => ({
          itemId: field,
          title,
        }))
    );
    const pageFilterStore = useTabulatorPageFilterStore();
    const activePageFilters = computed(() => pageFilterStore.activePageFilters);

    const computedInitialFilters = computed(() => Array.from(new Set(
      [
        ...props.immutableFilters.map(
          ({ field, condition: type, value }) => ({ field, type, value })
        ),
        ...activePageFilters.value]
    )));

    const poll = ref(null);


    const activeColumns = ref(
      props.activeColumns.length
        ? props.activeColumns
        : availableColumns.value.map(({ itemId }) => itemId)
    );
    const columns = computed(() => {
      return mutateColumns(
        props.columns
          .filter(({ field }) => !props.hiddenColumns.includes(field))
          .filter(({ field }) =>
            props.canSelectColumns ? activeColumns.value.includes(field) : true
          )
      );
    });

    const ajaxColumns = computed(() => {
      return columns.value.reduce((out, column) => {
        if (column.ajax) {
          out.push(column);
        }

        if (column.columns) {
          column.columns.forEach((subColumn) => {
            if (subColumn.ajax) {
              out.push(subColumn);
            }
          });
        }

        return out;
      }, []);
    });

    if (tabulatorElement.value) {
      const urlParams = readUrlQueryParams();
    }

    const options = computed(() => {
      const rowFormatters = props.columns.reduce((carry, { field, rowFormatter }) => {
        if (TabulatorRowFormatter.hasOwnProperty(rowFormatter?.name)) {
          carry.push([TabulatorRowFormatter[rowFormatter.name], field, rowFormatter.params || {}]);
        }

        return carry;
      }, [])

      const dataTreeOptions = props.hasTreeLayout
        ? {
          dataTree: true,
          dataTreeChildField: 'children',
          // 'caret-right'/'caret-down' have no Cygnus glyph — 'chevron-right'/
          // 'chevron-down' are the nearest available expand/collapse arrows.
          dataTreeExpandElement: `<span class="me-2 text-secondary">${cygnusIconHtml('chevron-right')} ${cygnusIconHtml('folder')} </span>`,
          dataTreeCollapseElement: `<span class="me-2 text-secondary">${cygnusIconHtml('chevron-down')} ${cygnusIconHtml('folder')} </span>`,
          dataTreeChildIndent: 15,
          rowFormatter: function (row) {
            const element = row.getElement();
            if (!row.getTreeChildren().length) {
              // has no children
              element.classList.add("bg-light");
            } else {
              // folder
              element.classList.add("bg-secondary");
            }
          },
        }
        : {};
      return {
        autoColumns: !Array.isArray(props.columns) || !props.columns.length,
        columns: columns.value,
        rowFormatter: (row) => {
          rowFormatters.forEach(([rowFormatter, field, params]) => {
            rowFormatter(row, row.getCell(field), params);
          })

          if (props.footerRowCount && (row.getPosition() - 1) >= (row.getTable().getDataCount() - props.footerRowCount)) {
            row.getElement().classList.add('tabulator-footer-row');
          }
        },
        // maxHeight: '80vh',

        layout: props.layout,
        filterMode: "remote",
        initialHeaderFilter: props.initialHeaderFilter,
        headerSort: false,
        initialSort: props.initialSort,
        ajaxRequestFunc: tabulatorAjaxRequestViaApi(api),
        ajaxURL: props.url,
        ajaxParams: () => {
          return {
            subEntityKey: props.subEntity,
            //pass componentId for caching
            componentId: props.componentId,
          };
        },
        pagination: props.pagination,
        paginationMode: "remote",
        paginationSize: 20,
        paginationSizeSelector: props.paginationSizes,
        paginationCounter: "rows",

        persistenceMode: props.persistence,
        persistence: props.persistence
          ? {
            sort: true,
            headerFilter: true,
            page: true,
          }
          : false,
        persistenceWriterFunc: props.persistence ? write : null,
        persistenceReaderFunc: props.persistence ? read : null,

        sortMode: "remote",
        initialFilter: computedInitialFilters.value,
        dependencies: {
          DateTime,
        },
        ...dataTreeOptions,
      };
    });

    function mutateColumns(columns) {

      let [..._columns] = columns.map((column) =>
        TabulatorColumnMapper.map(column)
      );

      props.actions.forEach(({ name, params }) => {
        const action = actionMapper(name);
        if (action) {
          action(_columns, params, api, router)
        }
      });

      if (columns.length) {
        _columns[_columns.length - 1].resizable = false;
      }

      _columns = groupColumns(_columns);

      return _columns;
    }

    function groupColumns(columns) {
      return columns.reduce((carry, column) => {
        if (column.columnGroupTitle === null) {
          carry.push(column);

          return carry;
        }

        const colGroup = carry.find((_column) => _column.title === column.columnGroupTitle);
        if (colGroup) {
          colGroup.columns.push(column);
        } else {
          carry.push({
            title: column.columnGroupTitle,
            columns: [column],
            cssClass: "tabulator-column-group-first tabulator-column-group-last",
          });
        }

        return carry;
      }, [])
    }

    function loadAjaxColumns() {
      ajaxColumns.value.forEach(async (column) => {
        ajaxColumnLoader(
          tabulator.value,
          props.ajaxUrl,
          column.field,
          column.ajax
        );
      });
    }

    watch(activeColumns, () => {
      if (tabulator.value) {
        tabulator.value.setColumns(columns.value);
        loadAjaxColumns();
        saveLayout();
      }
    })

    watch(() => pageFilterStore.activePageFilters, (newFilters) => {
      if (tabulator.value) {
        tabulator.value.setFilter(newFilters);
      }
    });

    function createTable() {
      const _options = {
        ...options.value,
      };
      tabulator.value = new Tabulator(tabulatorElement.value, _options);

      // hashListener({...tabulator.value});

      tabulator.value.on("tableBuilt", () => {
        queryListener(tabulator.value);

        // Setup polling after table is fully built
        if(props.shouldPollData) {
          poll.value = setInterval(() => {
              const ajaxParams = {
                subEntityKey: props.subEntity,
                componentId: props.componentId,
                _refresh: Date.now() // Cache buster
              };

              tabulator.value.replaceData(props.url, ajaxParams);
          }, 5000);
        }
      });

      function persistHorizontalScroll(value) {
        if (!value && scrollX.value > 20) {
          //tabulator resets scrollLeft to 0 when data loads, if it's a big jump it's likely not user scrolling so we don't want to update scrollX
          //hopefully tabulator will fix this in a future release
          return;
        }
        scrollX.value = value;
      }

      if (tabulator.value?.externalEvents.events.scrollHorizontal) {
        tabulator.value.off("scrollHorizontal", persistHorizontalScroll);
      }

      scrollX.value = 0; // reset scrollX when the component is mounted

      tabulator.value.on("scrollHorizontal", persistHorizontalScroll);

      tabulator.value.on("dataFiltered", function () {
        nextTick(() => {
          tabulator.value.rowManager.getElement().scrollLeft = scrollX.value;
          const componentId = props.componentId;
          const activeHeaderFilters = tabulator.value.getHeaderFilters();

          pageFilterStore.setActiveHeaderFiltersByInstance(componentId, activeHeaderFilters);

        });

        // if page filter set and it shares a header filter - disable the header filter
        const pageFilters = Object.values(pageFilterStore.pageFilters);
        const sharedFields = pageFilters
          .map(({ field }) => field)
          .filter((field) =>
            tabulator.value.getColumns().some(
              (column) =>
                column.getDefinition().headerFilter &&
                column.getField() === field
            )
          );
        const activePageFilterFields = pageFilterStore.activePageFilters.map(({ field }) => field);

        if (activePageFilterFields.some(field => sharedFields.includes(field))) {
          //disable header filter
          sharedFields.forEach((field) => {
            const input = tabulator.value
              .getColumn(field)
              .getElement()
              .querySelector("input");
            if (input) {
              input.disabled = true;
              //set placeholder to indicate disabled
              input.placeholder = "GLOBAL FILTER ACTIVE";
            }
          });
        } else {
          //enable header filters
          sharedFields.forEach((field) => {
            const input = tabulator.value
              .getColumn(field)
              .getElement()
              .querySelector("input");
            if (input) {
              input.disabled = false;
              //reset placeholder
              input.placeholder = "";
            }
          });
        }
      });

      function removePaddingFromTabulatorHeaders() {
        if (tabulatorElement.value instanceof HTMLElement) {
          tabulatorElement.value
            .querySelectorAll(".tabulator-header-filter > input")
            .forEach((el) => el.style.removeProperty("padding"));
        }
      }

      //set width 100% on tabulator-col-content
      function setWidth100OnTabulatorColContent() {
        if (tabulatorElement.value instanceof HTMLElement) {
          tabulatorElement.value
            .querySelectorAll(".tabulator-col-content")
            .forEach((el) => (el.style.width = "100%"));
        }
      }

      tabulator.value.on("renderComplete", removePaddingFromTabulatorHeaders);
      tabulator.value.on("renderComplete", setWidth100OnTabulatorColContent);
      tabulator.value.on("renderComplete", addStopPropagationToLinks);
      tabulator.value.on("renderComplete", () =>
        clearSearchElementPolyfill(
          tabulatorElement.value?.querySelectorAll(
            '.tabulator-header-filter > input[type="search"]'
          ),
          (field) => tabulator.value.setHeaderFilterValue(field, "")
        )
      );

      // If subEntity set, emit filtered event
      if (props.subEntity) {
        tabulator.value.on("dataFiltered", (filters) => {
          eventBus.$emit("tabulator:filtered", filters);
        });
      }

      if (props.ajaxUrl) {
        tabulator.value.on("renderComplete", () => {
          loadAjaxColumns();
        });
      }

      function addStopPropagationToLinks() {
        if (tabulatorElement.value instanceof HTMLElement) {
          tabulatorElement.value
            .querySelectorAll(".tabulator-cell a")
            .forEach((el) =>
              el.addEventListener("click", (e) => e.stopPropagation())
            );
        }
      }

      tabulator.value.on("cellClick", (e, row) => {
        if (isPropagationStopped(e)) {
          return;
        }

        e.stopPropagation();
        console.log('cellClick')
      });


      tabulator.value.on("rowClick", (e, row) => {
        if (isPropagationStopped(e)) {
          return;
        }
        console.log('rowClick')
      });

      props.onRowClickActions.forEach(({ action, data }) => {
        const method = resolveOnRowClickMethod(action);

        if (method) {
          tabulator.value.on("rowClick", (e, row) => {
            method(data, row, tabulator, e);
          });
        }
      });

      props.onCellClickActions.forEach(({ action, data }) => {
        const method = resolveOnCellClickMethod(action);

        if (method) {
          tabulator.value.on("cellClick", (e, cell) => {
            method(data, cell, tabulator, e);
          });
        }
      });

      onUpdated(removePaddingFromTabulatorHeaders);

      emit("tabulator:created", tabulator.value);
    }

    function saveLayout() {
      if (!props.saveLayoutRoute) {
        return;
      }
      api.post(props.saveLayoutRoute, {
        viewId: props.viewId,
        layout: activeColumns.value,
      });
    }

    function addFiltersFromEventBus(filters) {
      if (tabulator.value?.getFilters(true).toString() === filters.toString()) {
        return; //no change
      }
      tabulator.value.setFilter(filters);
    }


    onMounted(function () {
      createTable();
      if (props.hasTreeLayout) {
        //set visibility of pagination to hidden
        tabulator.value?.footerManager?.containerElement?.style?.setProperty('visibility', 'hidden');
      }
      // If subEntity not set,  listen for tabulator:filtered event
      // (probably not ideal - could pass 'shouldReceiveFilterEvent' or similar for better clarity, or make a wrapper component for aggregate tables?)

      if (!props.subEntity) {
        eventBus.$on("tabulator:filtered", addFiltersFromEventBus);
      }
    });

    onBeforeUnmount(function () {
      eventBus.$off('tabulator:filtered', addFiltersFromEventBus);
      if (!props.subEntity) clearTabulatorCache();
      if (poll.value) {
        clearInterval(poll.value);
      }
    });

    function handleGeneralSearch({ target: { value } }) {
      if (tabulator.value) {
        tabulator.value.setFilter([{ field: "_general", type: "like", value }]);
      }
    }

    return {
      tabulatorElement,
      options,
      activeColumns,
      availableColumns,
      handleGeneralSearch,
    };
  },
};
</script>

<style lang="scss">
@use "../../../node_modules/tabulator-tables/dist/css/tabulator_bootstrap5";

.tabulator {
  font-size: 0.9em;
  box-shadow: 0 3px 15px 1px rgba(0, 0, 0, 0.25);
}

.tabulator-tableholder {
  min-height: max-content !important;
}

.generalSearch {
  margin-bottom: 1em;

  &__label {
    display: block;
    font-weight: bold;
  }

  &__input {
    border-radius: 0.25rem;
  }
}
</style>
